<template>
  <header class="header-section" id="header"
          :class="{
            'is-fixed': (fixedHeader || !isAccountPage && isShopPage && currentPartner.code),
            'is-checkout': isCheckoutPage,
            'grow-layer': layerIndex
          }"
          @click="hideMicrocart"
  >
    <div class="header-section-inner">

      <!-- Partner logo -->
      <div class="header-section-block location-lang-block" :class="{'is-fixed-shop': isShopPage}" >
        <div class="flex desktop header-section-block__lang">
          <div class="pointer mr10" :class="{'active-lan': activeLen === 'uk'}" @click="changesLan('uk-UA')">УКР</div> |
          <div class="pointer ml10" :class="{'active-lan': activeLen === 'en'}" @click="changesLan('en-US')">EN</div>
        </div>
        <address-select v-if="currentLocation && !isShopPage && !isAccountPage && !isCMSPage && !isCheckoutPage && !isThankYouPage"/>
        <router-link v-if="isCheckoutPage && !isThankYouPage" class="header-section-block-link no-underline"
                     :to="localizedRoute(returnToShoppingLink)"
        >{{ $t('Return to shopping') }}</router-link>
        <router-link class="header-section-block-link picture-link no-underline"
                     :to="localizedRoute(returnToPartnerLink)"
                     v-if="isShopPage && !isAccountPage && !isCMSPage && !isCheckoutPage && !isThankYouPage"
        >
          <img :src="`${logoPath}`" alt="partner-logo" :width="logoSize.width" :height="logoSize.height" loading="eager" fetchpriority="high">
        </router-link>
      </div>
      <!---->

      <!-- Korzyna logo -->
      <div class="header-section-block">
        <logo width="29" height="31" />
      </div>
      <!---->
      <!-- User -->
      <div class="header-section-block" v-if="!isCheckoutPage || isThankYouPage">
        <div v-if="(isShopPage || isGiftPage) && !isCMSPage && !isAccountPage" class="header-section-block-shipping relative pointer" @click="showScheduleModal">
          {{ $t('Shipping') }}
        </div>
        <account-icon class="account-btn right-side" :fixed-header="fixedHeader" />
        <microcart-icon id="microcart-btn" v-if="cartIcon" class="header-section-block-btn microcart-btn"/>
      </div>
      <div class="header-section-block" v-else>
        <a href="#" v-if="!currentUser" class="header-section-block-link align-right no-underline" @click.prevent="gotoAccount">
          {{ $t('Login to your account') }}
        </a>
        <span v-else class="header-section-block-text align-left">
          {{ $t('You are logged in as') }} {{ currentUser.firstname }}
        </span>
        <account-icon class="account-btn right-side" v-if="currentUser" :fixed-header="fixedHeader" />
      </div>
      <!---->

    </div>
  </header>
</template>

<script async>
import { mapState, mapGetters, mapActions } from 'vuex'
import CurrentPage from 'theme/mixins/currentPage'
import config from 'config'
import clickOutside from 'theme/directives/click-outside'
import { s3Path } from '@vue-storefront/core/helpers';
const AddressSelect = () => import(/* webpackChunkName: "vsf-component-address-select" */ 'theme/components/core/blocks/Header/AddressSelect')
const AccountIcon = () => import(/* webpackChunkName: "vsf-component-account-icon" */ 'theme/components/core/blocks/Header/AccountIcon');
const Logo = () => import(/* webpackChunkName: "vsf-component-logo" */ 'theme/components/core/Logo');
const MicrocartIcon = () => import(/* webpackChunkName: "vsf-component-microcart-icon" */ 'theme/components/core/blocks/Header/MicrocartIcon');

const HEADER_HEIGHT = 57

const correctPath = (url) => {
  return (url.charAt(0) === '/') ? url : '/' + url
}

export default {
  name: 'Header',
  components: {
    AccountIcon,
    Logo,
    MicrocartIcon,
    AddressSelect
  },
  mixins: [CurrentPage],
  directives: {
    clickOutside
  },
  props: {
    cartIcon: {
      required: false,
      default: true,
      type: Boolean
    },
    layerIndex: {
      type: Boolean,
      required: false
    }
  },
  data () {
    return {
      navVisible: true,
      isScrolling: false,
      scrollTop: 0,
      lastScrollTop: 0,
      navbarHeight: 54,
      from: '',
      fixedHeader: false
    }
  },
  beforeMount () {
    window.addEventListener('scroll', this.onScroll, { passive: true })

    this.scrollInterval = setInterval(() => {
      if (this.isScrolling) {
        this.hasScrolled()
        this.isScrolling = false
      }
    }, 250)
  },
  beforeDestroy () {
    window.removeEventListener('scroll', this.onScroll)
    clearInterval(this.scrollInterval)
  },
  computed: {
    ...mapGetters({
      currentPartner: 'partners/getCurrentPartner',
      displayMode: 'partners/getCurrentPartnerDisplayMode',
      currentLocation: 'locations/getActiveLocation'
    }),
    ...mapState({
      isOpenLogin: state => state.ui.signUp,
      currentUser: state => state.user.current,
      isPartnerPage: state => state.ui.partnerPage
    }),
    logoPath () {
      return s3Path(this.currentPartner.logo_path, config.images.partner_header_logo.width, config.images.partner_header_logo.height);
    },
    logoSize () {
      return config.images.partner_header_logo;
    },
    isThankYouPage () {
      return this.$store.state.checkout.isThankYouPage ? this.$store.state.checkout.isThankYouPage : false
    },
    getCurrentLocationCode() {
      return (this.$route.params && !!this.$route.params['locationCode'])
        ? correctPath(this.$route.params.locationCode) : (this.currentLocation && !!this.currentLocation['code'])
          ? correctPath(this.currentLocation.code) : false
    },
    returnToShoppingLink () {
      return (this.getCurrentLocationCode || '/')
    },
    returnToPartnerLink () {
      return (this.getCurrentLocationCode && this.currentPartner && !!this.currentPartner['code'])
        ? correctPath(`${this.getCurrentLocationCode}/${this.currentPartner.code}`) : correctPath(this.getCurrentLocationCode)
    },
    isAccountPage () {
      let links = ['my-order', 'my-orders', 'my-account', 'my-shipping-details', 'my-payment-details']
      return links.includes(this.$route.name)
    },
    isCMSPage () {
      let links = ['about-us', 'contacts', 'FAQ', 'umovi-dostavki', 'terms-of-use', 'privacy', 'vacancies']
      return links.includes(this.$route.name)
    },
    isShopPage () {
      return this.currentPartner && this.currentPartner.code !== 'anywhere' && this.currentPartner.display_mode === 'shop'
    },
    isGiftPage () {
      return this.$route.name === 'gift'
    },
    activeLen () {
      return this.$root.$i18n.locale === 'en-US' ? 'en' : 'uk'
    }
  },
  methods: {
    ...mapActions({
      loadCart: 'cart/load'
    }),
    gotoAccount () {
      this.$bus.$emit('modal-toggle', 'modal-signup')
    },
    onScroll () {
      this.isScrolling = true
      this.fixedHeader = window.pageYOffset > HEADER_HEIGHT
    },
    hasScrolled () {
      this.scrollTop = window.scrollY
      this.navVisible = !(this.scrollTop > this.lastScrollTop && this.scrollTop > this.navbarHeight)
      this.lastScrollTop = this.scrollTop
    },
    backPage () {
      window.history.length > 1 ? this.$router.go(-1) : this.$router.push(this.currentPartner.route);
    },
    showScheduleModal () {
      this.$bus.$emit('show-schedule-modal')
      this.$bus.$emit('modal-show', 'modal-schedule')
    },
    changesLan (ln) {
      this.$store.dispatch('languages/changesCurrenLanguage', ln);
    },
    hideMicrocart (e) {
      let elementId = e.srcElement.getAttribute('id')
      let parentElementId = e.srcElement.parentElement.getAttribute('id')
      if (this.$store.state.ui.microcart && !(elementId === 'microcart-btn' || parentElementId === 'microcart-btn')) this.$store.commit('ui/setMicrocart', false)
    }
  }
}
</script>

<style lang="scss" scoped>
@import '~theme/css/variables/colors';
@import '~theme/css/helpers/functions/color';
@import '~theme/css/layout/screen';
$color-icon-hover: color(secondary, $colors-background);

.desktop {
  @media (max-width: 576px) {
    display: none;
  }
}

.header-section {
  width: 100vw;

  background: #fff;
  z-index: 5;

  &.is-fixed {
    position: fixed;
    top: 0;

    @media screen and (max-width: 576px) {
      & .is-fixed-shop {
        display: block;
      }
    }
  }

  &.is-checkout {
    @media screen and (max-width: 576px) {
      & .header-section-block:first-child {
        display: block;
      }
    }
  }

  &.grow-layer {
    z-index: 8;
  }

  &-inner {
    width: 1140px;
    height: 57px;
    margin: auto;
    padding: 0 15px;

    display: flex;
    justify-content: space-between;
    align-items: center;

    @media screen and (max-width: 1200px) {
      width: 946px;
    }

    @media screen and (max-width: 991px) {
      width: 722px;
    }

    @media only screen and (max-width: $screen-md) {
      width: 530px;
    }

    @media screen and (max-width: 576px) {
      width: 100%;
      box-sizing: border-box;
    }
  }

  &-block {
    display: flex;
    align-items: center;

    &__lang {
      min-width: 80px;
    }

    &:first-child, &:last-child {
      flex-basis: 33.33%;

      @media only screen and (max-width: $screen-md) {
        flex-basis: 45%;
      }
    }

    &:first-child {
      justify-content: flex-start;
    }

    &:last-child {
      justify-content: flex-end;
    }

    &:nth-child(2) {
      flex-basis: 33.33%;
      justify-content: center;

      @media only screen and (max-width: $screen-md) {
        flex-basis: 10%;
      }

      @media screen and (max-width: 576px) {
        flex-basis: 30%;
      }
    }

    &-link {
      margin-right: 10px;

      color: #bdbdbd;
      font-size: 14px;
      text-decoration: underline;

      @media only screen and (max-width: $screen-md) {
        font-size: 12px;
      }

      &.picture-link {
        width: auto;
        height: 57px;

        overflow: hidden;
      }

      & img {
        width: 115px;
        height: 57px;
        object-fit: contain;
        object-position: left;

        @media only screen and (max-width: $screen-md) {
          width: 75%;
        }
      }
    }

    &-text {
      margin-right: 10px;
      font-size: 14px;

      @media only screen and (max-width: $screen-md) {
        display: none;
      }
    }

    &-arrow {
      padding: 15px;
      display: none;

      color: #000;
      border: none;
      background: none;

      @media (max-width: 1200px) {
        display: block;

        span {
          font-size: 24px;
        }
      }
    }

    &-btn {
      padding: 15px;

      &:hover {
        cursor: pointer;
        background-color: $color-icon-hover;
      }
    }
    &-shipping {
      &:hover {
        color: #11bb2e;
      }
      @media only screen and (max-width: $screen-md) {
        font-size: 12px;
      }
    }
  }

  .location-lang-block {
    column-gap: 16px;

    @media only screen and (max-width: $screen-md) {
      .header-section-block__lang {
        order: 2;
      }

      .local-center-select {
        order: 1;
      }
    }
  }
}

.microcart-btn {
  color: #000;
}
.active-lan {
  color: #11bb2e;
  background: white;
}
</style>
